// PAGCanvas.jsx
import React, { useRef, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import getPag, {
  waitForPagExecuteChain,
  destroyPagFileOnChain,
} from '../resource/getPag.js';

// Gift PAGs cover the whole player area, so an uncapped devicePixelRatio of 3
// (most iPhones) more than doubles the pixels flushed per frame versus 2, with
// no visible gain on a short-lived animation.
const MAX_BACKING_DEVICE_PIXEL_RATIO = 2;

export const PAGCanvas = ({
  src = '',
  shouldPlay = true,
  onPlay = null,
  onEnd = null,
  onError = null,
  repeat = 1,
  fullscreen = false,
  textReplacements = null,
}) => {
  const canvasRef = useRef(null);
  const playerRef = useRef(null);
  const pagFileRef = useRef(null);
  const mountedRef = useRef(true);
  const endTimerRef = useRef(null);
  const textReplacementsRef = useRef(textReplacements);

  // PAGs with text replacements (entrance animation) size by their own aspect
  // ratio; plain PAGs (gift animation) keep the original fill behavior.
  const hasReplacements = !!textReplacements;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    textReplacementsRef.current = textReplacements;
  }, [textReplacements]);

  const cleanupPlayer = useCallback(() => {
    const player = playerRef.current;
    if (player) {
      if (endTimerRef.current) {
        clearTimeout(endTimerRef.current);
      }
      player.stop?.();
      player.dispose?.();
      playerRef.current = null;
    }

    // The entrance-animation file backs the view's composition, so destroy it
    // on the libpag serial chain *after* the view's queued stop()/destroy() —
    // destroying it synchronously here would free WASM memory the in-flight
    // flush loop still reads (use-after-free). It can also be set with no
    // player when load aborted mid-flight (non-cached path); enqueueing keeps
    // that destroy serialized too, and prevents the WASM leak.
    const pagFile = pagFileRef.current;
    if (pagFile) {
      pagFileRef.current = null;
      destroyPagFileOnChain({ pagFile });
    }
  }, []);

  // Fill-the-container sizing for plain PAGs (gift animation); paired with
  // `object-fit: contain` in the styled canvas.
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.style.width = '100%';
    canvas.style.height = '100%';

    const cappedDevicePixelRatio = Math.min(
      window.devicePixelRatio || 1,
      MAX_BACKING_DEVICE_PIXEL_RATIO
    );
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, rect.width * cappedDevicePixelRatio);
    canvas.height = Math.max(1, rect.height * cappedDevicePixelRatio);
  }, []);

  const handleEnd = useCallback(() => {
    if (!mountedRef.current) return;

    // clear timeout
    if (endTimerRef.current) {
      clearTimeout(endTimerRef.current);
      endTimerRef.current = null;
    } else {
      return;
    }

    cleanupPlayer();
    onEnd?.();
  }, [onEnd, cleanupPlayer]);

  const loadPAG = useCallback(async () => {
    const canvas = canvasRef.current;

    if (!canvas || !src || !shouldPlay || !mountedRef.current) return;

    cleanupPlayer();
    setError(null);
    setIsLoading(true);

    try {
      const hasReplacementsAtLoad = !!textReplacementsRef.current;

      const { isPAGSupported, pag } = await getPag({
        imageSource: src,
        shouldUseCache: !hasReplacementsAtLoad,
      });

      if (hasReplacementsAtLoad) {
        pagFileRef.current = pag?.file ?? null;
      }

      if (!mountedRef.current) return;

      if (!isPAGSupported || !pag) {
        throw new Error('PAG format not supported');
      }

      const currentTextReplacements = textReplacementsRef.current;

      if (currentTextReplacements) {
        for (const { index, text } of currentTextReplacements) {
          try {
            const textData = pag.file.getTextData(index);
            textData.text = text;
            pag.file.replaceText(index, textData);
          } catch (_) {
            // skip failed text replacement
          }
        }
      }

      if (hasReplacementsAtLoad) {
        // Entrance animation: lock the canvas backing to the PAG's native
        // size instead of measuring the container. The notice mounts
        // dynamically, so a measured backing can be low-res and get stretched
        // up by width:100% (the blur). Native size always downscales to the
        // display, staying sharp. Paired with useScale:false at init() below.
        canvas.width = Math.max(1, pag.width || 300);
        canvas.height = Math.max(1, pag.height || 150);
        canvas.style.width = '100%';
        canvas.style.height = 'auto';
      } else {
        // Gift animation fills a fixed-size container (object-fit: contain).
        // With useScale:false below, libpag no longer re-measures the
        // container at init, so an unlaid-out container (rect 0, clamped to
        // 1×1 by resizeCanvas) must fall back to the PAG's native size or the
        // animation renders into a 1×1 backing.
        resizeCanvas();
        if (canvas.width <= 1 || canvas.height <= 1) {
          canvas.width = Math.max(1, pag.width || 300);
          canvas.height = Math.max(1, pag.height || 150);
        }
      }

      // Wait until libpag is free, then stop if this card already unmounted —
      // otherwise we'd open a WebGL context for a gift the user already left.
      await waitForPagExecuteChain();
      if (!mountedRef.current) return;

      // Both paths pass useScale:false so libpag keeps the backing set above
      // instead of re-deriving it at init from the container's computed style
      // × the uncapped devicePixelRatio — which would blow past the
      // MAX_BACKING_DEVICE_PIXEL_RATIO cap (gift path) or shrink the
      // native-resolution backing (entrance path).
      const player = await pag.getPlayer(canvas, { useScale: false });
      if (!mountedRef.current) {
        player?.dispose?.();
        return;
      }

      playerRef.current = player;
      player.setRepeatCount?.(repeat);

      if (player._view?.addListener) {
        player._view.addListener('onAnimationEnd', handleEnd);
      }

      const duration = player.getDuration?.() || pag.duration || 0;
      if (duration > 0) {
        endTimerRef.current = setTimeout(handleEnd, duration + 100);
      }

      player.play?.();
      onPlay?.();

      if (player._view?.flush) {
        player._view.flush();
      }

      setIsLoading(false);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err.message);
      setIsLoading(false);
      onError?.(err);
    }
  }, [
    src,
    shouldPlay,
    repeat,
    onPlay,
    onError,
    cleanupPlayer,
    resizeCanvas,
    handleEnd,
  ]);

  useEffect(() => {
    const timer = setTimeout(loadPAG, 50);
    return () => clearTimeout(timer);
  }, [loadPAG]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      cleanupPlayer();
    };
  }, [cleanupPlayer]);

  return (
    <StyledPAGCanvas
      $autoHeight={hasReplacements}
      $isLoading={isLoading}
      $error={error}
      $fullscreen={fullscreen}
    >
      <canvas ref={canvasRef} />
    </StyledPAGCanvas>
  );
};

PAGCanvas.propTypes = {
  src: PropTypes.string,
  shouldPlay: PropTypes.bool,
  onPlay: PropTypes.func,
  onEnd: PropTypes.func,
  onError: PropTypes.func,
  repeat: PropTypes.number,
  fullscreen: PropTypes.bool,
  textReplacements: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number.isRequired,
      text: PropTypes.string.isRequired,
    })
  ),
};

const StyledPAGCanvas = styled.div`
  width: 100%;
  height: ${({ $autoHeight }) => ($autoHeight ? 'auto' : '100%')};

  canvas {
    ${({ $autoHeight, $isLoading, $error }) =>
      $autoHeight
        ? `
    width: 100%;
    display: block;
    `
        : `
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: opacity 0.3s ease-in-out;
    opacity: ${$isLoading ? 0.7 : $error ? 0.5 : 1};
    `}

    ${({ $fullscreen }) =>
      $fullscreen &&
      `
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
  `}
  }
`;

export default PAGCanvas;
